import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Loader2, CheckCircle2, ExternalLink, ArrowDownToLine } from "lucide-react";
import {
    fetchPayoutStatus,
    startPayoutOnboarding,
    openPayoutDashboard,
    fetchPayoutBalance,
    withdrawPayoutBalance,
} from "../lib/api";

// Lending page top banner. Shows the owner's Lendly credit balance and,
// depending on Stripe onboarding state, either a Withdraw button (fires
// a Stripe transfer to their connected bank) or a Connect payouts button.
//
// The old "you must connect Stripe before accepting bookings" gate has
// been removed — owners can list + accept freely, credits accrue
// automatically, and Stripe onboarding is only needed at withdrawal time.
const PayoutStatusBanner = () => {
    const [status, setStatus] = useState(null);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        try {
            const [s, b] = await Promise.all([
                fetchPayoutStatus(),
                fetchPayoutBalance(),
            ]);
            setStatus(s);
            setBalance(b);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await refresh();
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // On return from Stripe hosted onboarding, refresh balance + status.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const flag = params.get("payouts");
        if (flag === "connected") {
            toast.success("Payout setup submitted", {
                description:
                    "Verifying with Stripe now. Once verified you can tap Withdraw to send your credits to your bank.",
                duration: 8000,
            });
            refresh();
            window.history.replaceState({}, "", window.location.pathname);
        } else if (flag === "refresh") {
            toast.message("Onboarding was interrupted — try the link again.");
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    const onSetup = async () => {
        try {
            setBusy(true);
            const { url } = await startPayoutOnboarding();
            window.location.href = url;
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Couldn't start Stripe onboarding — try again shortly.",
            );
            setBusy(false);
        }
    };

    const onManage = async () => {
        try {
            setBusy(true);
            const { url } = await openPayoutDashboard();
            window.open(url, "_blank", "noopener,noreferrer");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't open Stripe dashboard.");
        } finally {
            setBusy(false);
        }
    };

    const onWithdraw = async () => {
        try {
            setBusy(true);
            const res = await withdrawPayoutBalance();
            toast.success(`Withdraw of $${res.amount?.toFixed(2)} started`, {
                description:
                    "Money is on its way to your Stripe-connected bank (typically 5–10 business days). You'll get an email when it lands.",
                duration: 9000,
            });
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Withdrawal failed.");
        } finally {
            setBusy(false);
        }
    };

    if (loading) return null;

    const amount = balance?.balance ?? 0;
    const hasBalance = amount > 0;
    const stripeConnected = !!balance?.stripe_connected;
    const hasStripeAccount = !!balance?.has_stripe_account;

    // Case 1 — Balance to withdraw, Stripe fully connected → prominent Withdraw
    if (hasBalance && stripeConnected) {
        return (
            <div
                data-testid="payouts-banner-withdraw"
                className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-[#737a3d]/40 bg-[#F4F1EA] px-4 py-4"
            >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#737a3d] text-[#F4F1EA]">
                    <Wallet className="h-5 w-5" strokeWidth={2.5} />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="font-display text-xl font-bold text-[#2e3220]">
                        ${amount.toFixed(2)} available
                    </p>
                    <p className="mt-0.5 text-xs text-[#5a6142]">
                        Your Lendly credits — tap Withdraw to send them to your Stripe-linked bank.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        data-testid="payouts-manage-btn"
                        onClick={onManage}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#737a3d]/40 bg-transparent px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#737a3d] transition-colors hover:bg-[#737a3d]/10 disabled:opacity-50"
                    >
                        <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
                        Stripe dashboard
                    </button>
                    <button
                        type="button"
                        data-testid="payouts-withdraw-btn"
                        onClick={onWithdraw}
                        disabled={busy}
                        className="inline-flex items-center gap-2 rounded-full bg-[#737a3d] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] transition-colors hover:bg-[#5a6142] disabled:opacity-50"
                    >
                        {busy ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
                        ) : (
                            <ArrowDownToLine className="h-3.5 w-3.5" strokeWidth={2.5} />
                        )}
                        Withdraw ${amount.toFixed(2)}
                    </button>
                </div>
            </div>
        );
    }

    // Case 2 — Balance to withdraw, but Stripe NOT yet connected → nudge
    if (hasBalance && !stripeConnected) {
        return (
            <div
                data-testid="payouts-banner-connect-to-withdraw"
                className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-[#B56C5A]/40 bg-[#F4F1EA] px-4 py-4"
            >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#B56C5A] text-[#F4F1EA]">
                    <Wallet className="h-5 w-5" strokeWidth={2.5} />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="font-display text-xl font-bold text-[#2e3220]">
                        ${amount.toFixed(2)} in credits
                    </p>
                    <p className="mt-0.5 text-xs text-[#5a6142]">
                        Connect Stripe to withdraw your earnings to your bank. Takes ~2 minutes.
                        You keep listing and accepting rentals — this is only needed to cash out.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="payouts-setup-btn"
                    onClick={onSetup}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-full bg-[#B56C5A] px-4 py-2 text-xs font-bold uppercase tracking-widest text-[#F4F1EA] transition-colors hover:bg-[#a55c4a] disabled:opacity-50"
                >
                    {busy ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
                    ) : (
                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={2.5} />
                    )}
                    {hasStripeAccount ? "Finish Stripe setup" : "Connect payouts"}
                </button>
            </div>
        );
    }

    // Case 3 — Zero balance, Stripe connected → subtle active chip
    if (stripeConnected) {
        return (
            <div
                data-testid="payouts-banner-active"
                className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-[#737a3d]/25 bg-[#737a3d]/8 px-4 py-3 text-sm"
            >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#737a3d] text-[#F4F1EA]">
                    <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#2e3220]">
                        Payouts connected · $0.00 available
                    </p>
                    <p className="text-xs text-[#5a6142]">
                        Once neighbors borrow your items, your 90% cut will appear here to withdraw.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="payouts-manage-btn"
                    onClick={onManage}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#737a3d]/40 bg-transparent px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-[#737a3d] transition-colors hover:bg-[#737a3d]/10 disabled:opacity-50"
                >
                    <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
                    Manage
                </button>
            </div>
        );
    }

    // Case 4 — Nothing earned yet, Stripe not connected → hide (removed
    // the mandatory setup nudge; Stripe is now only prompted when the
    // owner actually has money to withdraw).
    return null;
};

export default PayoutStatusBanner;
